/* eslint-disable indent */
import IMask from "imask";
import { initFileUploads } from "../../modules/image-upload/image-upload.js";
import { notifyErrorWithText } from "../../modules/notifications/index.js";
import { API } from "../../services/api/endpoints/index.js";
import { yclientsCodes, loadYclientsCodes } from "../../services/api/endpoints/yclients/codes.js";
import { setDefaultInputListeners } from "../../ui/input/index.js";
import { mergeObjects } from "../../utils/merge-objects/index.js";
import { initCalendar } from "./calendar.js";
import { initSelect } from "../../ui/select/index.js";
import { parseCenterInfoFromHTMLStr } from "./util.js";

function getDateValue(item) {
    return typeof item === "string" ? item : item?.date;
}

function getServiceTypeRef(item) {
    return typeof item === "string" ? undefined : item?.serviceTypeRef;
}

function mergeDateObjects(...dateLists) {
    const result = [];
    const addedKeys = new Set();

    dateLists.flat().forEach((item) => {
        const date = getDateValue(item);

        if (!date) return;

        const serviceTypeRef = typeof item === "string" ? undefined : item.serviceTypeRef;
        const key = `${date}:${serviceTypeRef || ""}`;

        if (!addedKeys.has(key)) {
            result.push(typeof item === "string" ? { date } : item);
            addedKeys.add(key);
        }
    });

    return result;
}

/**
 *
 * @param {string} serviceType
 * @param {number} employeeId
 * @returns {Promise<
 *   import("../../services/api/endpoints/yclients/index.js").ScheduleBranch |
 *   import("../../services/api/endpoints/yclients/index.js").ScheduleBranchNoItems
 * >}
 */
async function getScheduleBranch(serviceType, employeeId = null) {
    const isOnline = serviceType === "online" && Boolean(yclientsCodes.kgd);

    // if online merge data from online and kgd services
    if (isOnline) {
        const onlineData = await API.yclients.getScheduleBranch(serviceType, null, employeeId);

        const onlineSchedule = await onlineData.json();

        const kgdData = await API.yclients.getScheduleBranch("kgd", null, employeeId);

        const kgdSchedule = await kgdData.json();

        const onlineRecordingDates = Array.isArray(onlineSchedule.recording_dates)
            ? onlineSchedule.recording_dates
            : [];

        const kgdRecordingDates = Array.isArray(kgdSchedule.recording_dates)
            ? kgdSchedule.recording_dates
            : [];

        const onlineWaitingListDates = Array.isArray(onlineSchedule.waiting_list_dates)
            ? onlineSchedule.waiting_list_dates
            : [];

        const kgdWaitingListDates = Array.isArray(kgdSchedule.waiting_list_dates)
            ? kgdSchedule.waiting_list_dates
            : [];

        const resultSchedule = mergeObjects(onlineSchedule, kgdSchedule);
        resultSchedule.waiting_list_dates = mergeDateObjects(
            onlineWaitingListDates,
            kgdWaitingListDates,
        );

        if (onlineRecordingDates.length === 0) {
            // если у онлайна отсутствует recording_dates,
            // берем dates как даты, в которых точно есть запись
            resultSchedule.recording_dates = [
                ...kgdRecordingDates,
                ...onlineSchedule.dates.map((date) => ({ date })),
            ];
        }

        return resultSchedule;
    }

    // return service data
    const serviceData = await API.yclients.getScheduleBranch(serviceType, null, employeeId);

    const serviceSchedule = await serviceData.json();

    return serviceSchedule;
}

function resetPopup(serviceType) {
    const popup = document.getElementById(`popup--appointment-${serviceType}`);

    if (!popup) return;

    popup.classList.remove("popup--success");

    popup.classList.remove("popup--error");

    popup.classList.remove("popup--no-place");

    popup.classList.remove("popup--redirect");

    const popupTitle = popup.querySelector(".popup__head .h3");

    popupTitle.innerText = "Запись на консультацию";

    const backButton = popup.querySelector(".popup__back-btn");

    backButton.style.display = null;

    const contactsStepEl = popup.querySelector(`#contacts_step-${serviceType}`);

    const timeStepEl = popup.querySelector(`#time_step-${serviceType}`);

    const doctorsStepEl = popup.querySelector(`#doctors_step-${serviceType}`);

    const errorBlock = popup.querySelector(".notification--error .notification__title");

    if (errorBlock) {
        errorBlock.innerHTML = "Произошла ошибка<br/>";
    }

    if (contactsStepEl) {
        contactsStepEl.remove();
    }

    if (timeStepEl) {
        timeStepEl.remove();
    }

    if (doctorsStepEl) {
        doctorsStepEl.remove();
    }

    const stepIndicators = popup.querySelectorAll(".step-indicator__button.selected");

    stepIndicators.forEach((i) => {
        i.classList.remove("selected");
    });

    const doctors = popup.querySelectorAll(".card__content-info--doctor").forEach((d) => {
        d.remove();
    });

    popup.querySelector("#step-indicator__button-calendar").classList.add("selected");
    popup.querySelector(`#calendar_step-${serviceType}`).classList.add("show");
}

function closeAllAppointmentPopups() {
    const popups = document.querySelectorAll("popup--appointment");

    popups.forEach((popup) => {
        popup.classList.remove("open");
    });
    sessionStorage.removeItem("doctor-appointment");
}

export function closeAppointmentPopup(serviceType) {
    const popup = document.getElementById(`popup--appointment-${serviceType}`);

    if (popup) {
        popup.classList.remove("open");
        resetPopup(serviceType);
    }
    sessionStorage.removeItem("doctor-appointment");

    const event = new CustomEvent("popup-close");

    popup.dispatchEvent(event);
}

export async function openAppointmentPopup(serviceType) {
    closeAllAppointmentPopups();

    let calendarInstance = null;

    const popup = document.getElementById(`popup--appointment-${serviceType}`);

    if (popup) {
        try {
            await loadYclientsCodes();
            if (!yclientsCodes[serviceType]) throw new Error("Не найден филиал");
            const storedDoctor = await new Promise((resolve) => {
                setTimeout(() => {
                    const doctor = sessionStorage.getItem("doctor-appointment");
                    if (doctor) {
                        resolve(JSON.parse(doctor));
                    } else {
                        resolve(null);
                    }
                }, 200);
            });

            const doctorsStepIndicator = popup.querySelector("#step-indicator__button-doctors");

            if (storedDoctor) {
                doctorsStepIndicator.classList.add("hidden");
            }
            popup.classList.add("open");

            popup.classList.add("popup--loading");
            const schedule = storedDoctor
                ? await getScheduleBranch(serviceType, storedDoctor.doctor.id)
                : await getScheduleBranch(serviceType);

            const dates = [];

            let highlitedDates = Array.isArray(schedule.recording_dates)
                ? schedule.recording_dates
                : [];
            const waitingListDates = schedule.waiting_list_dates || [];
            const waitingListDateSet = new Set(
                waitingListDates.map(getDateValue).filter(Boolean),
            );

            const cardContent = popup.querySelector(".card.card--entity .card__content");

            if (schedule.dates) {
                dates.push(...Object.values(schedule.dates));
            }

            const availableDateSet = new Set(dates);
            highlitedDates = highlitedDates.filter((item) => {
                const date = getDateValue(item);
                if (getServiceTypeRef(item)) {
                    return true;
                }
                if (!date) {
                    return false;
                }
                if (waitingListDateSet.has(date) && !availableDateSet.has(date)) {
                    return false;
                }
                return availableDateSet.has(date);
            });

            if (highlitedDates.length === 0) {
                dates.forEach((d) => {
                    const date = typeof d === "string" ? d : d?.date ?? d;
                    if (date && !waitingListDateSet.has(date)) {
                        highlitedDates.push({ date });
                    }
                });
            } else {
                dates.forEach((d) => {
                    const date = typeof d === "string" ? d : d?.date ?? d;
                    if (!date || !availableDateSet.has(date)) {
                        return;
                    }
                    const alreadyHighlighted = highlitedDates.some(
                        (item) => getDateValue(item) === date && !getServiceTypeRef(item),
                    );
                    if (!alreadyHighlighted) {
                        highlitedDates.push({ date });
                    }
                });
            }

            popup.dataset.waitingListDates = JSON.stringify(
                waitingListDates.map(getDateValue).filter(Boolean),
            );


            if (storedDoctor) {
                cardContent.innerHTML += `
                <div class="card__content-info card__content-info--doctor">
                        <i class="icon icon-user" aria-label="icon user"></i>
                        <p class="p">${storedDoctor.doctor.name}</p>
                </div>
                `;
                const isMultipleFilials = storedDoctor.doctor.info.addresses
                    && Object.keys(storedDoctor.doctor.info.addresses).length > 1;

                if (isMultipleFilials) {
                    const featuresStr = localStorage.getItem("skinDiagnosticCenterFeatures");

                    const filials = [];

                    const companyIds = Object.keys(storedDoctor.doctor.info.addresses) || [];

                    const serviceTypes = Array.from(
                        new Set([
                            serviceType,
                            ...highlitedDates
                                .filter((d) => d.serviceTypeRef !== undefined)
                                .map((d) => d.serviceTypeRef),
                            ...companyIds
                                .map((id) => {
                                    const entry = Object.values(yclientsCodes).find(
                                        (item) => String(item.companyId) === String(id),
                                    );
                                    return entry?.serviceType;
                                })
                                .filter(Boolean),
                        ]),
                    );

                    const features = featuresStr ? JSON.parse(featuresStr).map((feature) => ({
                        ...parseCenterInfoFromHTMLStr(feature.properties.balloonContentBody),
                        city: feature.city,
                    })) : {};

                    serviceTypes.forEach((st) => {
                        const feature = features.find((f) => f.serviceType === st);
                        const filialDates = st === serviceType
                            ? highlitedDates.filter((d) => !d.serviceTypeRef)
                            : highlitedDates.filter((d) => d.serviceTypeRef === st);

                        const filialWaitingListDates = st === serviceType
                            ? waitingListDates.filter((d) => !d.serviceTypeRef)
                            : waitingListDates.filter((d) => d.serviceTypeRef === st);

                        if (feature) {
                            filials.push({
                                serviceType: st,
                                feature,
                                dates: filialDates,
                                waitingListDates: filialWaitingListDates,
                            });
                        }
                    });

                    const selectEl = document.createElement("div");

                    selectEl.classList.add("select", "select_like-input", "input");

                    selectEl.innerHTML += `
                                        <select id="select-address">
                                            <option value="all">Все филиалы</option>
                                            ${filials.map((f) => (
                        `<option value="${f.serviceType}">${f.feature.address}</option>`
                    ))}
                                        </select>
                                    `;

                    const addressEl = popup.querySelector(".card__content-info-address");
                    addressEl.replaceWith(selectEl);

                    popup.addEventListener("popup-close", () => {
                        selectEl.replaceWith(addressEl);
                        doctorsStepIndicator.classList.remove("hidden");
                    });

                    const allFilialsDateClick = (dateStr, isWaitingList) => {
                        if (isWaitingList) {
                            selectEl.dataset.error = "Выберите филиал";
                            return false;
                        }
                        if (selectEl.dataset.error) {
                            delete selectEl.dataset.error;
                        }
                        return true;
                    };
                    const calendarEl = popup.querySelector(`#appointment__calendar-${serviceType}`);

                    const hasWaitingList = yclientsCodes[serviceType]
                        .waitingEmployeeId > 0;

                    if (calendarInstance) {
                        calendarInstance.destroy();
                    }
                    popup.classList.remove("popup--loading");

                    calendarInstance = initCalendar({
                        calendarEl,
                        freeDates: dates,
                        hasWaitingList,
                        highlitedDates,
                        waitingListDates,
                        forceDisableNonHighlightedDates: serviceType === "online",
                        shouldHighlightRedirect: false,
                        onDateClick: allFilialsDateClick,
                    });

                    document.querySelector("#select-address").addEventListener("change", (e) => {
                        const selectedServiceType = e.target.value;
                        if (selectEl.dataset.error) {
                            delete selectEl.dataset.error;
                        }
                        popup.classList.remove("popup--no-place");


                        if (selectedServiceType === "all") {
                            // eslint-disable-next-line no-shadow
                            const hasWaitingList = yclientsCodes[serviceType]
                                .waitingEmployeeId > 0;


                            if (calendarInstance) {
                                calendarInstance.destroy();
                            }
                            calendarInstance = initCalendar({
                                calendarEl,
                                freeDates: dates,
                                hasWaitingList,
                                highlitedDates,
                                waitingListDates,
                                forceDisableNonHighlightedDates: serviceType === "online",
                                shouldHighlightRedirect: false,
                                onDateClick: allFilialsDateClick,
                            });
                        } else {
                            const filial = filials
                                .find((f) => f.serviceType === selectedServiceType);

                            // eslint-disable-next-line no-shadow
                            const hasWaitingList = yclientsCodes[selectedServiceType]
                                .waitingEmployeeId > 0;
                            calendarInstance.destroy();

                            calendarInstance = initCalendar({
                                calendarEl,
                                freeDates: filial.dates,
                                hasWaitingList,
                                highlitedDates: filial.dates,
                                waitingListDates: filial.waitingListDates,
                                commonServiceTypeRef: filial.serviceType,
                                forceDisableNonHighlightedDates: serviceType === "online",
                                shouldHighlightRedirect: false,
                            });
                        }
                    });
                } else {
                    const calendarEl = popup.querySelector(`#appointment__calendar-${serviceType}`);

                    popup.classList.remove("popup--loading");

                    const hasWaitingList = yclientsCodes[serviceType].waitingEmployeeId > 0;

                    calendarInstance = initCalendar({
                        calendarEl,
                        freeDates: dates,
                        hasWaitingList,
                        highlitedDates,
                        waitingListDates,
                        forceDisableNonHighlightedDates: serviceType === "online",
                        shouldHighlightRedirect: false,
                    });
                }
            } else {
                const calendarEl = popup.querySelector(`#appointment__calendar-${serviceType}`);

                const hasWaitingList = yclientsCodes[serviceType].waitingEmployeeId > 0;
                popup.classList.remove("popup--loading");

                cardContent.querySelector(".card__content-info--doctor")?.remove();
                calendarInstance = initCalendar({
                    calendarEl,
                    freeDates: dates,
                    hasWaitingList,
                    highlitedDates,
                    waitingListDates,
                    forceDisableNonHighlightedDates: serviceType === "online",
                    shouldHighlightRedirect: false,
                });
            }

            popup.addEventListener("popup-close", () => {
                calendarInstance.destroy();
            });
        } catch (error) {
            closeAppointmentPopup(serviceType);
            notifyErrorWithText(error.message);
            console.error(error);
        }
    }
    initSelect();
}

function initPhoneMask() {
    const phoneMaskSdcInputs = document.querySelectorAll(".form-sdc input[data-mask=\"phone\"]");

    phoneMaskSdcInputs.forEach((input) => {
        // По UX ведем себя ближе к front-new: поле стартует с +7 без
        // принудительного плейсхолдера 9, но в мобильный формат нормализуем
        // первые цифры и дополнительно валидируем на submit.
        const imask = IMask(input, {
            mask: "+{7} 000 000 00 00",
            lazy: true,
            placeholderChar: "",
        });

        const syncValueAttribute = () => {
            input.setAttribute("value", input.value);
        };

        const normalizePhoneValue = () => {
            let digits = input.value.replace(/\D/g, "");

            if (digits === "") {
                imask.value = "+7";
                syncValueAttribute();
                return;
            }

            if (digits.startsWith("9")) {
                digits = `7${digits}`;
            } else if (digits.startsWith("8")) {
                digits = `7${digits.slice(1)}`;
            } else if (digits.startsWith("78") || digits.startsWith("77")) {
                digits = `7${digits.slice(2)}`;
            }

            if (digits === "7") {
                imask.value = "+7";
                syncValueAttribute();
                return;
            }

            if (digits !== imask.unmaskedValue) {
                imask.unmaskedValue = digits.slice(0, 11);
            }

            syncValueAttribute();
        };

        imask.on("accept", syncValueAttribute);

        if (!input.value) {
            imask.value = "+7";
            syncValueAttribute();
        } else {
            normalizePhoneValue();
        }

        input.addEventListener("focus", () => {
            if (!input.value) {
                imask.value = "+7";
            }

            requestAnimationFrame(() => {
                const len = input.value.length;
                input.setSelectionRange(len, len);
            });
        });

        input.addEventListener("input", () => {
            normalizePhoneValue();

            if (imask.masked.isComplete) {
                input.setCustomValidity("");
            } else {
                input.setCustomValidity("Введите полный номер");
            }
        });
    });
}

/**
 *
 * @param {{
 *  address:string,
 *  city: string,
 *  schedule: import("../../services/api/endpoints/yclients/index.js").ScheduleBranchItemSchedule[],
 *  serviceType:string
 *  date: string,
 *  employee: import("../../services/api/endpoints/yclients/index.js").ScheduleBranchEmployee,
 *  time: string
 * }} param0
*/
function generateContactsStep({
    address,
    city,
    serviceType,
    date,
    employee,
    employeeItem,
    time,
    timeToShow,
    isWaitingList = false,
    isRedirect,
}) {
    const contactsStepEl = document.createElement("div");

    const isOnline = serviceType === "online";

    contactsStepEl.classList.add("appointment__step", "show");

    contactsStepEl.id = `contacts_step-${serviceType}`;

    let serviceId = yclientsCodes[serviceType].serviceId;

    let companyId = yclientsCodes[serviceType].companyId;

    let serviceTypeResult = serviceType;

    if (isOnline) {
        serviceTypeResult = employeeItem.info.type;
        serviceId = yclientsCodes[employeeItem.info.type].serviceId;

        companyId = yclientsCodes[employeeItem.info.type].companyId;
    }

    contactsStepEl.innerHTML = `
        <div class="card card--entity">
            <div class="card__head">
                <h4 class="h5">${BX.message('YOUR_VISIT_DATA')}</h4>
            </div>
            <div class="card__content">
                <div class="card__content-info">
                    <i class="icon icon-pin" aria-label="icon pin"></i>
                    ${serviceType === "online"
            ? "<p class=\"p\">" + BX.message('ONLINE') + "</p>"
            : `<p class="p">г. ${city}, ${address}</p>`
        }
                </div>
                <div class="card__content-info">
                    <i class="icon icon-calendar" aria-label="icon calendar"></i>
                    <p class="p">${date}</p>
                </div>
                <div class="card__content-info">
                    <i class="icon icon-clock" aria-label="icon clock"></i>
                    <p class="p">${timeToShow} ${serviceType === "online" ? " (МСК, UTC+3)" : ""}</p>
                </div>
                ${employee.name ? `
                    <div class="card__content-info">
                        <i class="icon icon-user" aria-label="icon user"></i>
                        <p class="p">${employee.name}</p>
                    </div>
                    ` : ""
        }
                
            </div>
        </div>
        ${isRedirect ? `
            <div style="height: unset; margin: 0" class="notification notification--redirect notification--warning notification--fullwidth">
                <div  class="notification__title">
                    <div class="notification__title">
                        ${BX.message('ATTENTION')}<br>
                        ${BX.message('SERVICE_ON_DATE')} <span class="date">${date}</span> ${BX.message('ON_OTHER_ADDRESS')}<br/>
                        ${BX.message('AUTOREDIRECT_SYSTEM')}:<br/><span class="address">${address}</span>
                        <br>
                    </div>
                </div>
            </div>
        ` : ""}
        <div class="step__content">
            <h4 class="h6">${BX.message('FILL_CONTACT_DATA')}</h4>
            <form class="form form-sdc" method="post" enctype="multipart/form-data">
                <input hidden name="workerId" value="${employee.id}">
                <input hidden name="companyId" value="${companyId}">
                <input hidden name="serviceId" value="${serviceId}">
                <input hidden name="serviceType" value="${serviceTypeResult}">
                <input hidden name="globalServiceType" value="${serviceType}">
                <input hidden name="datetime" value="${date}T${time}:00">
                <input hidden name="action" value="setRecord">
                <input hidden name="form" value="schedule">
                ${isWaitingList ? `
                    <input type="hidden" name="isWaitingList" value="Y">
                    ` : ""
        }
                ${isOnline ? `
                    <input type="hidden" name="isOnline" value="Y">
                    ` : ""
        }
                <input hidden name="yclients_agreement" value="Y">
                <input type="file" class="hidden" accept="image/jpeg, image/png" multiple hidden name="faceImages[]">
                <input hidden name="isWaitingList">
                <div class="card card--toast">
                    <i class="icon icon-info" aria-label="icon info"></i>
                    <div class="card__message">
                        <p class="p">${BX.message('THREE_NOTICE')}</p>
                    </div>
                </div>
                <div class="card card--toast">
                    <i class="icon icon-info" aria-label="icon info"></i>
                    <div class="card__message">
                        <p class="p">${BX.message('CAMERA_NOTICE')}</p>
                    </div>
                </div>
                <div class="form__input input input--text">
                    <label class="label">
                        <input type="text" name="fullname" required>
                        <span class="p">${BX.message('NAME')}</span>
                    </label>
                </div>
                <div class="form__input input input--text">
                    <label class="label">
                        <input type="email" name="email" required>
                        <span class="p">${BX.message('EMAIL')}</span>
                    </label>
                </div>
                <div class="form__input input input--text">
                    <label class="label">
                        <input data-mask="phone" type="tel" name="phone" required>
                        <span class="p">${BX.message('PHONE')}</span>
                    </label>
                </div>
                <div class="form__input input input--text">
                    <label class="label">
                        <input type="text" name="tg">
                        <span class="p">${BX.message('TELEGRAM_USERNAME')}</span>
                    </label>
                </div>
                <div class="form__input input input--file card">
                    <div class="input__uploading">
                        <div class="loader" style="width: 20px;height: 20px;"></div>

                        <p class="p">${BX.message('FILE_LOADING')}</p>
                    </div>
                    <div class="input__info">
                        <p class="p input__title">${BX.message('LOAD_YOUR_PHOTO')}</p>
                        <p class="p input__hint">${BX.message('PHOTO_WARNING')}</p>
                        <div class="input__file-list">
                            <div class="input__file">
                                <img src="" alt="">
                                <button class="button button--ghost" type="button" title="Delete image">
                                    <i class="icon icon-close"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <label class="button button--secondary--light input__upload">
                        <input type="file" class="hidden" name="images[]" accept="image/jpeg, image/png" multiple>
                        <i class="icon icon-makephoto" aria-label="Icon photo"></i>
                        <span>${BX.message('LOAD')}</span>
                    </label>
                </div>
                <div class="form__input">
                    <div>${BX.message('I_AGREE')}:</div>
                    <div class="p p--80 form__agree">
                        <label class="label yc-label" for="personal_egree_1">
                            <input class="input--checkbox" type="checkbox" required name="personal_egree_1" id="personal_egree_1" value="ok">
                            <span class="p">
                                ${BX.message('GELTEK_AGREEMENT')}
                            </span>
                        </label>
                    </div>
                    <div class="p p--80 form__agree">
                        <label class="label yc-label" for="personal_egree_1">
                            <input class="input--checkbox" type="checkbox" required name="personal_egree_1" id="personal_egree_1" value="ok">
                            <span class="p">
                                ${BX.message('YCLIENTS_AGREEMENT')}
                            </span>
                        </label>
                    </div>

                    ${jsUser && jsUser.showAdAgreement ? `
                    <div class="p p--80 form__agree">
                        <label class="label yc-label" for="ad_agreement">
                            <input class="input--checkbox" type="checkbox" name="ad_agreement" id="ad_agreement" value="ok">
                            <span class="p">
                                ${BX.message('ADV_AGREEMENT')}
                            </span>
                        </label>
                    </div>
                    ` : ""
        }

                    <br>
                    <button class="button button--primary--light form__submit" type="submit">${BX.message('SIGN')}</button>
                </div>
                <p class="p p--80 p--r color-text--g-80">
                    ${BX.message('RESTRICTIONS')}
                </p>
            </form>
        </div>
    `;

    async function handleSubmit(event) {
        event.preventDefault();
        const form = event.target;
        const popupEl = contactsStepEl.closest(".popup");

        const phoneInput = form.querySelector('input[name="phone"]');
        const phoneDigits = (phoneInput?.value || "").replace(/\D/g, "");
        if (!/^79\d{9}$/.test(phoneDigits)) {
            if (phoneInput) {
                phoneInput.setCustomValidity("Введите корректный мобильный номер в формате +7 9XX XXX XX XX");
                phoneInput.reportValidity();
                phoneInput.focus();
            }
            return;
        }
        if (phoneInput) phoneInput.setCustomValidity("");

        popupEl.classList.add("popup--loading");

        const formData = new FormData(form);

        try {
            const response = await API.yclients.postScheduleBranch(formData);

            const data = await response.json();
            if (data.success) {
                popupEl.classList.remove("open");
                popupEl.classList.add("open");

                popupEl.classList.add("popup--success");

                popupEl.classList.remove("popup--loading");

                // notifyAboutSuccessAppointment(data.message);
                const popupHead = popupEl.querySelector(".popup__head .h3");

                if (isWaitingList) {
                    popupHead.innerText = "Спасибо, вы успешно записаны в лист ожидания";
                } else {
                    popupHead.innerText = "Спасибо, вы успешно записаны на консультацию";
                }

                const backButton = popupEl.querySelector(".popup__back-btn");

                backButton.style.display = "none";

                const stepIndicator = popupEl.querySelector(".step-indicator");

                stepIndicator.style.display = "none";

                const stepContent = contactsStepEl.querySelector(".appointment__step.show .step__content");

                stepContent.style.display = "none";
                try {
                    ym(95112, "reachGoal", yclientsCodes[serviceType].ym);

                    // Доп. цели для московских филиалов
                    switch (yclientsCodes[serviceType].serviceType) {
                        case "msk":
                            ym(95112, "reachGoal", "zapis_cdk_novokyzneckaya");
                            break;
                        case "arbat":
                            ym(95112, "reachGoal", "zapis_cdk_arbat");
                            break;
                        case "moskva-m-tsvetnoy-bulvar":
                            ym(95112, "reachGoal", "zapis_cdk_cvetnoi_bulvar");
                            break;
                        case "krsk":
                            ym(95112,"reachGoal", "zapiskosmetologkrasnoyrsk");
                            break;
                    }
                } catch (e) {
                    console.warn(`yandex metrika не прошла serviceType: ${serviceType}, ym:${yclientsCodes[serviceType].ym}`);
                }
            } else {
                const errors = Object.values(data.errors);
                const stepContent = contactsStepEl.querySelector(".appointment__step.show .step__content");

                stepContent.style.display = "none";

                contactsStepEl.innerHTML += `
                    <div style="height: unset; margin: 0" class="notification notification--error notification--fullwidth">
                        <div  class="notification__title">
                            Произошла ошибка<br/>
                        </div>
                    </div>
                `;

                const errorBlock = popupEl.querySelector(".notification--error .notification__title");

                errors.forEach((erMsg) => {
                    const erElem = document.createElement("p");
                    erElem.classList.add("p");
                    erElem.innerHTML = erMsg;
                    errorBlock.append(erElem);
                });
                popupEl.classList.add("popup--error");
            }
        } catch (error) {
            notifyErrorWithText(error.message);
            console.error(error);
        } finally {
            popupEl.classList.remove("popup--loading");
        }
    }

    const submitForm = contactsStepEl.querySelector(".form");

    submitForm.addEventListener("submit", handleSubmit);

    return contactsStepEl;
}

/**
 *
 * @param {{
 *  address:string,
 *  city: string,
 *  schedule: import("../../services/api/endpoints/yclients/index.js").ScheduleBranchItemSchedule[],
 *  serviceType:string
 *  date: string,
 *  employee: import("../../services/api/endpoints/yclients/index.js").ScheduleBranchEmployee
 * }} param0
*/
function generateTimeStep({
    address,
    city,
    schedule,
    serviceType,
    date,
    employee,
    employeeItem,
    requestedServiceType,
    isRedirect,
}) {
    const timeStepEl = document.createElement("div");

    timeStepEl.classList.add("appointment__step", "show");

    timeStepEl.id = `time_step-${serviceType}`;

    timeStepEl.innerHTML = `
        <div class="card card--entity">
            <div class="card__head">
                <h4 class="h5">Данные вашего визита</h4>
            </div>
            <div class="card__content">
                <div class="card__content-info">
                    <i class="icon icon-pin" aria-label="icon pin"></i>
                    ${serviceType === "online"
            ? "<p class=\"p\">Онлайн</p>"
            : `<p class="p">г. ${city}, ${address}</p>`
        }   
                </div>
                <div class="card__content-info">
                    <i class="icon icon-calendar" aria-label="icon calendar"></i>
                    <p class="p">${date}</p>
                </div>
                <div class="card__content-info">
                    <i class="icon icon-user" aria-label="icon user"></i>
                    <p class="p">${employee.name}</p>
                </div>
            </div>
        </div>
        ${isRedirect ? `
            <div style="height: unset; margin: 0" class="notification notification--redirect notification--warning notification--fullwidth">
                <div  class="notification__title">
                    <div class="notification__title">
                        Внимание!<br>
                        Услуга, выбранная на указанную дату <span class="date">${date}</span> предоставляется по другому адресу.<br/>
                        Система автоматически перенаправит вашу запись в филиал по адресу:<br/><span class="address">${address}</span>
                        <br>
                    </div>
                </div>
            </div>
        ` : ""}
        <div class="step__content">
            <fieldset class="timeslots">
                <legend class="h6">Выберите время приёма${serviceType === "online" ? " (МСК, UTC+3)" : ""}</legend>
                ${schedule.map((scheduleItem) => {
            let timeToShow = scheduleItem.time;

            // if (serviceType === "online" && requestedServiceType === "kgd") {
            if (serviceType === "online") {
                const dateTime = new Date(scheduleItem.datetime);

                timeToShow = new Intl.DateTimeFormat("ru-RU", {
                    timeZone: "Europe/Moscow",
                    hour: "2-digit",
                    minute: "2-digit",
                }).format(dateTime);

                console.log(dateTime, timeToShow);
            }

            return `
                            <div class="timeslot">
                                <input class="hidden" type="radio" name="timeslotOnline" id="time_step-${scheduleItem.time}" value="${scheduleItem.time}">
                                <label class="label button button--secondary--light" for="time_step-${scheduleItem.time}">${timeToShow}</label>
                            </div>
                        `;
        }).join(" ")
        }
        </fieldset>
        </div>
    `;

    function handleTimeClick(event) {
        event.preventDefault();

        const timeSlot = event.currentTarget;

        const timeInput = timeSlot.querySelector("input");
        const timeLabel = timeSlot.querySelector(".label");

        const timeStr = timeInput.value;
        const timeStrToShow = timeLabel.innerText;

        const contactsStepEl = generateContactsStep({
            address,
            city,
            schedule,
            serviceType,
            date,
            employee,
            employeeItem,
            time: timeStr,
            timeToShow: timeStrToShow,
            isRedirect,
        });

        timeStepEl.classList.remove("show");

        timeStepEl.parentNode.append(contactsStepEl);

        setDefaultInputListeners();

        initPhoneMask();

        initFileUploads();

        const popupEl = timeStepEl.closest(".popup");

        const oldBackButton = popupEl.querySelector(".popup__back-btn");

        const backButton = oldBackButton.cloneNode(true);

        const contactsIndicatorEl = popupEl.querySelector("#step-indicator__button-contacts");

        contactsIndicatorEl.classList.add("selected");

        contactsIndicatorEl.previousElementSibling.classList.remove("selected");

        oldBackButton.replaceWith(backButton);

        backButton.addEventListener("click", () => {
            timeStepEl.classList.add("show");
            contactsStepEl.remove("show");
            popupEl.classList.remove("popup--error");

            contactsIndicatorEl.classList.remove("selected");

            contactsIndicatorEl.previousElementSibling.classList.add("selected");

            backButton.replaceWith(oldBackButton);
        });
    }

    const timeSlots = timeStepEl.querySelectorAll(".timeslot");

    timeSlots.forEach((timeSlot) => {
        timeSlot.addEventListener("click", handleTimeClick);
    });

    return timeStepEl;
}

/**
 *
 * @param {{
 *      address:string,
 *      city: string,
 *      schedule: import("../../services/api/endpoints/yclients/index.js").ScheduleBranch,
 *      serviceType:string
 *      date: string
 * }} param0
 */
function generateDoctorsStep({
    address,
    city,
    schedule,
    serviceType,
    date,
}) {
    const doctorsStepEl = document.createElement("div");

    const anyDoctorImgSrc = window.location.hostname === "localhost"
        ? "/img/upload/any-doctor.webp"
        : "/local/templates/geltek2024/frontend/img/upload/any-doctor.webp";

    doctorsStepEl.classList.add("appointment__step", "show");

    doctorsStepEl.id = `doctors_step-${serviceType}`;

    doctorsStepEl.innerHTML = `
    
        <div class="card card--entity">
            <div class="card__head">
                <h4 class="h5">Данные вашего визита</h4>
            </div>
            <div class="card__content">
                <div class="card__content-info">
                    <i class="icon icon-pin" aria-label="icon pin"></i>
                    ${serviceType === "online"
            ? "<p class=\"p\">Онлайн</p>"
            : `<p class="p">г. ${city}, ${address}</p>`
        }                    
                </div>
                <div class="card__content-info">
                    <i class="icon icon-calendar" aria-label="icon calendar"></i>
                    <p class="p">${date}</p>
                </div>
            </div>
        </div>
        <div class="step__content">
            <div class="doctors" role="radiogroup" aria-labelledby="doctors-appointment">
                <div id="any" class="doctors__item card card--image card--bordered"
                    role="radio" aria-checked="false" tabindex="0"
                    aria-labelledby="doctors-appointment-0" data-value="0">
                    <img class="card__image"
                        src="${anyDoctorImgSrc}" alt="Любой специалист">
                    <div class="card__content">
                        <h5 class="h5">Любой специалист</h5>

                    </div>
                </div>
                ${Object.entries(schedule.items).filter(([_, i]) => i.employee.name !== "Лист ожидания" && i.schedule !== "").map(([key, item], index) => (
            `
                        <div id="${key}" class="doctors__item card card--image card--bordered"
                            role="radio" aria-checked="false" tabindex="${index}"
                            aria-labelledby="doctors-appointment-${index}" data-value="${index}">
                            <img class="card__image"
                                src="${item.employee.avatar_big}" alt="${item.employee.name}">
                            <div class="card__content">
                                <h5 class="h5">${item.employee.name}</h5>
                                <p class="p">Рейтинг:${item.employee.rating}</p>

                            </div>
                        </div>
                        `
        )).join(" ")
        }
                
            </div>
        </div>
    `;

    function handleDoctorClick(event) {
        const doctorEl = event.currentTarget;

        let item = null;

        if (doctorEl.id === "any") {
            // get random employee
            const employeeIds = Object.keys(schedule.items);
            item = schedule.items[
                employeeIds[
                Math.floor(Math.random() * employeeIds.length)
                ]
            ];
        } else {
            item = schedule.items[doctorEl.id];
        }

        const employee = item?.employee;

        const employeeSchedule = item?.schedule;

        if (!employee || !employeeSchedule) return;

        if (typeof employeeSchedule === "string") {
            throw new Error(employeeSchedule);
        }
        const timeStepEl = generateTimeStep({
            address,
            city,
            schedule: employeeSchedule,
            serviceType,
            employee,
            employeeItem: item,
            date,
            requestedServiceType: item.info.type,
        });

        doctorsStepEl.classList.remove("show");

        doctorsStepEl.parentNode.append(timeStepEl);

        const popupEl = doctorsStepEl.closest(".popup");

        const oldBackButton = popupEl.querySelector(".popup__back-btn");

        const backButton = oldBackButton.cloneNode(true);

        const timeIndicatorEl = popupEl.querySelector("#step-indicator__button-time");

        timeIndicatorEl.classList.add("selected");

        timeIndicatorEl.previousElementSibling.classList.remove("selected");

        oldBackButton.replaceWith(backButton);

        backButton.addEventListener("click", () => {
            doctorsStepEl.classList.add("show");
            timeStepEl.remove("show");
            timeIndicatorEl.classList.remove("selected");

            timeIndicatorEl.previousElementSibling.classList.add("selected");

            backButton.replaceWith(oldBackButton);
        });
    }

    const doctorsEls = doctorsStepEl.querySelectorAll(".doctors__item");

    doctorsEls.forEach((doctorEl) => {
        doctorEl.addEventListener("click", handleDoctorClick);
    });

    return doctorsStepEl;
}

/**
 *
 * @param {{
 * city: string,
 * address:string,
 * phone:string,
 * workHours:string,
 * serviceType:string,
 * }} info
 * @returns
 */
export function generatePopUpBySkinCenterInfo(info) {
    const popupEl = document.createElement("div");
    popupEl.classList.add("popup", "popup--appointment");
    popupEl.id = `popup--appointment-${info.serviceType}`;
    popupEl.innerHTML = `

        <div class="popup__wrapper">
            <button class="button button--ghost--dark popup__close" type="button"
                    data-id="PopupOnlineAppointment"
                    title="Close popup">
                <i class="icon icon-close"></i>
            </button>
            <div class="popup__head">
                <button class="btn--M btn-ghost-light popup__back-btn"><i class="icon-arrow-left"></i><span>Назад</span></button>
                ${info.serviceType === "online"
            ? "<h3 class=\"h3\">Запись на онлайн консультацию</h3>"
            : "<h3 class=\"h3\">Запись на консультацию</h3>"
        }
            </div>
            <div class="popup__content appointment scrollbar">

                <ul class="step-indicator">
                    <li id="step-indicator__button-calendar" class="step-indicator__button selected" data-target="0">
                        <button class="button button--ghost" type="button">
                            <span class="button__icon">
                                <i class="icon icon-calendar" aria-label="Icon calendar"></i>
                            </span>
                            <span class="text">Дата</span>
                        </button>
                    </li>
                    <li id="step-indicator__button-doctors" class="step-indicator__button" data-target="1">
                        <button class="button button--ghost" type="button">
                            <span class="button__icon">
                                <i class="icon icon-user" aria-label="Icon user"></i>
                            </span>
                            <span class="text">Специалист</span>
                        </button>
                    </li>
                    <li id="step-indicator__button-time" class="step-indicator__button" data-target="2">
                        <button class="button button--ghost" type="button">
                            <span class="button__icon">
                                <i class="icon icon-clock" aria-label="Icon clock"></i>
                            </span>
                            <span class="text">Время</span>
                        </button>
                    </li>
                    <li id="step-indicator__button-contacts" class="step-indicator__button" data-target="3">
                        <button class="button button--ghost" type="button">
                            <span class="button__icon">
                                <i class="icon icon-phone" aria-label="Icon phone"></i>
                            </span>
                            <span class="text">Контакты</span>
                        </button>
                    </li>
                </ul>

                <div class="appointment__steps">
                
                    <!-- step date -->                    
                    <div id="calendar_step-${info.serviceType}" class="appointment__step step show" data-target="0">
                        <div class="card card--entity">
                            <div class="card__head">
                                <h4 class="h5">Данные вашего визита</h4>
                            </div>
                            <div class="card__content">
                                <div class="card__content-info card__content-info-address">
                                    <i class="icon icon-pin" aria-label="icon pin"></i>
                                    ${info.serviceType === "online"
            ? "<p class=\"p\">Онлайн</p>"
            : `<p class="p">г. ${info.city}, ${info.address}</p>`
        }
                                    
                                </div>
                            </div>

                            
                        </div>
                        
                        <div style="height: unset; margin: 0" class="notification notification--no-place notification--warning notification--fullwidth">
                            <div  class="notification__title">
                                Нет свободных мест<br/>
                                Вы можете позвонить или написать нам в WhatsApp, администратор внесёт вас в лист ожидания 
                                на удобную дату и предложит варианты, если какая-то запись не подтвердится
                                <br/>
                                Тел. <a href="tel:${info.phone}" >${info.phone}</a>
                                <br/><button style="margin-top:12px;" class="appointment__waiting-list btn--S btn-primary-light">Записаться в лист ожидания</button>
                            </div>
                        </div>
                        <div style="height: unset; margin: 0" class="notification notification--redirect notification--warning notification--fullwidth">
                            <div  class="notification__title">
                                <div class="notification__title">
                                Внимание!<br>
                                Услуга, выбранная на указанную дату <span class="date"></span> предоставляется по другому адресу.<br/>
                                Система автоматически перенаправит вашу запись в филиал по адресу:<br/><span class="address"></span>
                                <br>
                            </div>
                            </div>
                        </div>
                        <div class="step__content">
                            <h4 class="h6">Выберите дату приёма</h4>
                            <div id="appointment__calendar-${info.serviceType}" class="appointment__calendar" data-for="appointment__calendar-selected-date-${info.serviceType}"></div>
                            <input type="hidden" id="appointment__calendar-selected-date-${info.serviceType}" name="selectedDate" value="">
                        </div>
                    </div>
                
                </div>
            </div>
            <div class="loader" style="width: 20px;height: 20px;margin: 0 auto;left: 50%;"></div>
            
        </div>
    `;

    const stepsEl = popupEl.querySelector(".appointment__steps");

    async function calendarDateChange(event) {
        const backButton = popupEl.querySelector(".popup__back-btn");

        const dateStr = event.target.value;

        const serviceTypeRef = event.target.dataset.serviceTypeRef;

        const appointmentWaitingListButton = popupEl.querySelector(".appointment__waiting-list");
        let serviceTypeToFetch = info.serviceType;

        const resultData = {
            address: info.address,
            city: info.city,
            date: dateStr,
            serviceType: serviceTypeToFetch,
            isRedirect: false,
        };
        async function handleAppointmentWaitingList(e) {
            e.preventDefault();

            try {
                const waitingListResponse = await API.yclients.getFreeWaitingListSlot(
                    serviceTypeToFetch,
                    dateStr,
                );

                const data = await waitingListResponse.json();

                if (!data.datetime) {
                    throw new Error(`На дату ${dateStr} нет записи в лист ожидания`);
                }

                const [date, time] = data.datetime.split("T");

                const contactsStepEl = generateContactsStep({
                    address: resultData.address,
                    city: resultData.city,
                    serviceType: serviceTypeToFetch,
                    date,
                    employee: {
                        id: data.workerId,
                    },
                    time: time.slice(0, 5),
                    timeToShow: time.slice(0, 5),
                    isWaitingList: true,
                });
                stepsEl.append(contactsStepEl);

                setDefaultInputListeners();

                initPhoneMask();

                initFileUploads();

                const calendarStep = popupEl.querySelector(`#calendar_step-${info.serviceType}`);

                calendarStep.classList.remove("show");

                const clendarIndicatorEl = popupEl.querySelector("#step-indicator__button-calendar");

                clendarIndicatorEl.classList.remove("selected");
                clendarIndicatorEl.nextElementSibling.nextElementSibling.nextElementSibling.classList.add("selected");

                backButton.addEventListener("click", () => {
                    contactsStepEl.remove();
                    clendarIndicatorEl.classList.add("selected");
                    clendarIndicatorEl.nextElementSibling.nextElementSibling.nextElementSibling.classList.remove("selected");
                    calendarStep.classList.add("show");
                    backButton.style.display = null;
                });

                backButton.style.display = "flex";
            } catch (error) {
                notifyErrorWithText(error.message);
                console.error(error);
            }
        }

        popupEl.classList.add("popup--loading");
        try {
            if (serviceTypeRef) {
                const companyId = yclientsCodes[serviceTypeRef].companyId;

                if (companyId) {
                    const featuresStr = localStorage.getItem("skinDiagnosticCenterFeatures");

                    const features = featuresStr ? JSON.parse(featuresStr) : {};

                    const feature = features.find((f) => f.company_id === companyId.toString());

                    const attensionEl = popupEl.querySelector(".notification--redirect");

                    attensionEl.querySelector(".date").innerText = dateStr;
                    attensionEl.querySelector(".address").innerText = feature.address;

                    // popupEl.classList.add("popup--redirect");

                    serviceTypeToFetch = serviceTypeRef;

                    resultData.address = feature.address;

                    resultData.city = feature.city;

                    resultData.isRedirect = true;
                }
            }

            if (appointmentWaitingListButton) {
                const popupWaitingListDates = JSON.parse(popupEl.dataset.waitingListDates || "[]");
                const isWaitingListDate = popupWaitingListDates.includes(dateStr);
                const hasWaitingList = yclientsCodes[serviceTypeToFetch].waitingEmployeeId > 0;

                if (!hasWaitingList || !isWaitingListDate) {
                    appointmentWaitingListButton.style.display = "none";
                } else {
                    const newAppointmentWaitingListButton = appointmentWaitingListButton.cloneNode(true);

                    newAppointmentWaitingListButton.style.display = "";
                    appointmentWaitingListButton.replaceWith(newAppointmentWaitingListButton);
                    newAppointmentWaitingListButton.addEventListener("click", handleAppointmentWaitingList);
                }
            }

            const response = await API.yclients.getScheduleBranch(serviceTypeToFetch, dateStr);

            const schedule = await response.json();

            const storedDoctor = sessionStorage.getItem("doctor-appointment");

            let doctor = null;

            if (storedDoctor) {
                doctor = JSON.parse(storedDoctor);

                if (serviceTypeRef) {
                    const doctorInSchedule = schedule?.items && Object.values(schedule?.items)
                        .find((i) => i.employee.user_id === doctor.doctor.user_id);

                    if (doctorInSchedule) {
                        doctor.doctor = doctorInSchedule.employee;
                    }
                }
            }

            const hasEmployeesWithSchedule = schedule.items && Object.entries(schedule.items).filter(([_, item]) => item.employee.name !== "Лист ожидания" && item.schedule !== "").length > 0;

            if (!schedule.items || !hasEmployeesWithSchedule) {
                popupEl.classList.add("popup--no-place");
            } else if (
                doctor
                && !Object.keys(schedule?.items)?.includes(doctor.doctor.id.toString())
            ) {
                popupEl.classList.add("popup--no-place");
            } else {
                popupEl.classList.remove("popup--no-place");

                resultData.schedule = schedule;

                const calendarStep = popupEl.querySelector(`#calendar_step-${info.serviceType}`);

                const clendarIndicatorEl = popupEl.querySelector("#step-indicator__button-calendar");

                const doctorsStepEl = generateDoctorsStep({
                    address: resultData.address,
                    city: resultData.city,
                    schedule: resultData.schedule,
                    date: dateStr,
                    serviceType: serviceTypeToFetch,
                    isRedirect: resultData.isRedirect,
                });

                stepsEl.append(doctorsStepEl);

                if (doctor) {
                    const timeStepEl = generateTimeStep({
                        address: resultData.address,
                        city: resultData.city,
                        schedule: resultData.schedule.items[doctor.doctor.id].schedule,
                        date: dateStr,
                        serviceType: serviceTypeToFetch,
                        employee: doctor.doctor,
                        isRedirect: resultData.isRedirect,
                    });
                    stepsEl.append(timeStepEl);

                    doctorsStepEl.classList.remove("show");

                    clendarIndicatorEl.classList.remove("selected");

                    clendarIndicatorEl.nextElementSibling.nextElementSibling.classList.add("selected");

                    backButton.addEventListener("click", () => {
                        timeStepEl.remove();
                        clendarIndicatorEl.classList.add("selected");
                        popupEl.classList.remove("popup--redirect");
                        clendarIndicatorEl.nextElementSibling.nextElementSibling.classList.remove("selected");
                        calendarStep.classList.add("show");
                        backButton.style.display = null;
                    });
                } else {
                    clendarIndicatorEl.classList.remove("selected");

                    clendarIndicatorEl.nextElementSibling.classList.add("selected");

                    backButton.addEventListener("click", () => {
                        doctorsStepEl.remove();
                        popupEl.classList.remove("popup--error");
                        popupEl.classList.remove("popup--redirect");
                        clendarIndicatorEl.classList.add("selected");
                        clendarIndicatorEl.nextElementSibling.classList.remove("selected");
                        calendarStep.classList.add("show");
                        backButton.style.display = null;
                    });
                }
                calendarStep.classList.remove("show");

                backButton.style.display = "flex";
            }
        } catch (error) {
            notifyErrorWithText(error.message);
            console.error(error);
        } finally {
            popupEl.classList.remove("popup--loading");
        }
    }

    const closeButton = popupEl.querySelector(".popup__close");

    closeButton.addEventListener("click", () => closeAppointmentPopup(info.serviceType));

    const calendarInputEl = popupEl.querySelector(`#appointment__calendar-selected-date-${info.serviceType}`);

    calendarInputEl.addEventListener("change", calendarDateChange);

    return popupEl;
}
